import React from 'react';
import StakeholderFeedbackItem from "./StakeholderFeedbackItem";
import * as dateFormatter from '../../dateFormatter';
declare var kendo: any;

const FILTER_OPTIONS = {
    ALL: 'All',
    ACTIVE: 'Active',
    EXPIRED: 'Expired'
} as const;

const FEEDBACK_TYPES = {
    STAKEHOLDER: 'Stakeholder Feedback',
    CLOSED_COMMITTEE: 'Closed Committee'
} as const;

export interface IStakeholderFeedbackListProps { list: string }

interface SRFeedbackPages {
    committeeNames: Array<string>,
    dueDate: Date,  // Will be ISO format from JSON
    topics: Array<string>,
    name: string,
    description: string,
    linkURL: string,
    contentLink: string,
    active?: string
}

interface State {
    feedbacks: Array<SRFeedbackPages>,
    entity: string,
    topic: string,
    fromDate: string,
    toDate: string,
    active: string,
    entityList: Array<string>,
    topicList: Array<string>,
    yearList: Array<string>,
    activeYear: string,
    searchInput: string,
    loading: boolean
}

const LOCAL_STORAGE_FILTER_PREFIX = 'stakeholder-feedback-';

export class StakeholderFeedbackList extends React.Component<IStakeholderFeedbackListProps, State> {
    private startDateRef: React.RefObject<HTMLInputElement>;
    private endDateRef: React.RefObject<HTMLInputElement>;
    private startDatePicker: any;
    private endDatePicker: any;
    private searchDebounceTimer: NodeJS.Timeout | null = null;

    constructor(props: IStakeholderFeedbackListProps) {
        super(props);
        this.state = {
            feedbacks: [],
            entity: 'All',
            topic: 'All',
            fromDate: '',
            toDate: '',
            active: 'Active',
            searchInput: '',
            entityList: [],
            topicList: [],
            yearList: [],
            activeYear: '',
            loading: true
        };

        this.startDateRef = React.createRef();
        this.endDateRef = React.createRef();

        this.findLocallyStoredFilterValue = this.findLocallyStoredFilterValue.bind(this);
        this.findPreviousFilters = this.findPreviousFilters.bind(this);
        this.clearFilters = this.clearFilters.bind(this);
        this.clearDatePickers = this.clearDatePickers.bind(this);
        this.getKendoDateValue = this.getKendoDateValue.bind(this);
        this.handleKendoFromDateChange = this.handleKendoFromDateChange.bind(this);
        this.handleKendoToDateChange = this.handleKendoToDateChange.bind(this);
    }

    public componentDidMount() {
        this.initKendo();
        this.findPreviousFilters();
        this.getFeedback();
    }

    public componentWillUnmount() {
        // Clear debounce timer
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
        }
        
        // Cleanup Kendo date pickers
        if (this.startDatePicker) {
            this.startDatePicker.destroy();
        }
        if (this.endDatePicker) {
            this.endDatePicker.destroy();
        }
    }

    private findLocallyStoredFilterValue(filterKey: string): string | null {
        return localStorage.getItem(`${LOCAL_STORAGE_FILTER_PREFIX}${filterKey}`);
    }

    private findPreviousFilters() {
        const entityFilter = this.findLocallyStoredFilterValue('entity');
        const topicFilter = this.findLocallyStoredFilterValue('topic');
        const fromDateFilter = this.findLocallyStoredFilterValue('fromDate');
        const toDateFilter = this.findLocallyStoredFilterValue('toDate');
        const activeLevelFilter = this.findLocallyStoredFilterValue('active');
        const selectedYearFilter = this.findLocallyStoredFilterValue('activeYear');
        const searchInputFilter = this.findLocallyStoredFilterValue('searchInput');

        this.setState({
            entity: entityFilter ?? 'All',
            topic: topicFilter ?? 'All',
            fromDate: fromDateFilter ?? '',
            toDate: toDateFilter ?? '',
            active: activeLevelFilter ?? 'Active',
            activeYear: selectedYearFilter ?? new Date().getFullYear().toString(),
            searchInput: searchInputFilter ?? ''
        });
    }

    private preserveFilter(filterKey: string, filterValue: string) {
        localStorage.setItem(`${LOCAL_STORAGE_FILTER_PREFIX}${filterKey}`, filterValue);
    }

    private removeAllFiltersFromLocalStorage() {
        const filterKeys = ['entity', 'topic', 'fromDate', 'toDate', 'active', 'activeYear', 'searchInput'];
        filterKeys.forEach(key => localStorage.removeItem(`${LOCAL_STORAGE_FILTER_PREFIX}${key}`));
    }

    private getFeedback() {
        let url = '/api/ClosedCommitteeFeedback/GetClosedCommitteeFeedback';
        if (this.props.list === "Stakeholder Feedback") {
            url = '/api/Feedback/GetStakeHolderFeedback';
        }

        $.getJSON(url)
            .done((data) => {
                const pages = data.feedbackPages ?? [];
                const tempEntities = new Set<string>();
                const tempTopics = new Set<string>();
                const tempYearList = new Set<string>();
                const nowDate = new Date();
                let activeYear = dateFormatter.DateFormatter.Format(new Date(), "YYYY");

                for (const page of pages) {
                    // Committee names
                    page.committeeNames = page.committeeNames ?? [];
                    page.committeeNames.forEach(name => tempEntities.add(name));
                    
                    // Active/Expired status
                    const expireDate = new Date(page.dueDate);
                    page.active = nowDate > expireDate ? "Expired" : "Active";
                    
                    // Topics
                    page.topics = page.topics ?? [];
                    page.topics.forEach(topic => tempTopics.add(topic));
                    
                    // Years
                    const tempYear = dateFormatter.DateFormatter.Format(page.dueDate, "YYYY");
                    tempYearList.add(tempYear);
                }
                
                const entityList = Array.from(tempEntities).sort();
                const topicList = Array.from(tempTopics).sort();
                const yearList = Array.from(tempYearList).sort();

                if (yearList.indexOf(activeYear) === -1) {
                    activeYear = yearList.slice(-1).toString();
                }

                pages.sort((a: SRFeedbackPages, b: SRFeedbackPages) => 
                    new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());

                this.setState({
                    feedbacks: pages,
                    entityList: entityList,
                    topicList: topicList,
                    yearList: yearList,
                    loading: false
                });
            })
            .fail((jqXHR, textStatus, errorThrown) => {
                console.error('Failed to load feedback:', textStatus, errorThrown);
                this.setState({ loading: false });
            });
    }

    private initKendo() {
        if (this.startDateRef.current) {
            this.startDatePicker = $(this.startDateRef.current).kendoDatePicker({
                value: this.getKendoDateValue('fromDate'),
                change: this.handleKendoFromDateChange,
            }).data("kendoDatePicker");
        }

        if (this.endDateRef.current) {
            this.endDatePicker = $(this.endDateRef.current).kendoDatePicker({
                value: this.getKendoDateValue('toDate'),
                change: this.handleKendoToDateChange,
            }).data("kendoDatePicker");
        }
    }

    private getKendoDateValue(filterKey: string): Date | null {
        const storedDateValue = this.findLocallyStoredFilterValue(filterKey);
        return storedDateValue ? new Date(storedDateValue) : null;
    }

    private handleKendoFromDateChange(e: any) {
        const date = e.sender.value();
        if (date === null) {
            localStorage.removeItem(`${LOCAL_STORAGE_FILTER_PREFIX}fromDate`);
            this.setState({ fromDate: '' });
        } else {
            const dateString = date instanceof Date ? date.toISOString() : new Date(date).toISOString();
            this.setState({ fromDate: dateString });
            this.preserveFilter('fromDate', dateString);
        }
    }

    private handleKendoToDateChange(e: any) {
        const date = e.sender.value();
        if (date === null) {
            localStorage.removeItem(`${LOCAL_STORAGE_FILTER_PREFIX}toDate`);
            this.setState({ toDate: '' });
        } else {
            const dateString = date instanceof Date ? date.toISOString() : new Date(date).toISOString();
            this.setState({ toDate: dateString });
            this.preserveFilter('toDate', dateString);
        }
    }

    public filterYear(year: string) {
        this.setState({ activeYear: year });
        this.preserveFilter('activeYear', year);
    }

    public filterEntity(entity: string) {
        this.setState({ entity: entity });
        this.preserveFilter('entity', entity);
    }

    public filterTopic(topic: string) {
        this.setState({ topic: topic });
        this.preserveFilter('topic', topic);
    }

    public filterActive(active: string) {
        this.setState({ active: active });
        this.preserveFilter('active', active);
    }

    public filterSearchInput(searchInput: string) {
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
        }
        
        this.searchDebounceTimer = setTimeout(() => {
            this.setState({ searchInput: searchInput });
            this.preserveFilter('searchInput', searchInput);
        }, 300);
    }

    public clearFilters() {
        this.setState({
            entity: 'All',
            topic: 'All',
            fromDate: '',
            toDate: '',
            active: 'Active',
            searchInput: '',
            activeYear: new Date().getFullYear().toString()
        });
        this.clearDatePickers();
        this.removeAllFiltersFromLocalStorage();
    }

    public clearDatePickers() {
        if (this.startDatePicker) {
            this.startDatePicker.value(null);
        }
        if (this.endDatePicker) {
            this.endDatePicker.value(null);
        }
    }

    public filtersAreDefault(): boolean {
        return this.state.entity === 'All' &&
            this.state.topic === 'All' &&
            this.state.fromDate === '' &&
            this.state.toDate === '' &&
            this.state.active === 'Active' &&
            this.state.searchInput === '';
    }

    private matchesEntityFilter(feedback: SRFeedbackPages): boolean {
        return this.state.entity === 'All' || 
               this.state.entity === '' || 
               feedback.committeeNames.includes(this.state.entity);
    }

    private matchesTopicFilter(feedback: SRFeedbackPages): boolean {
        return this.state.topic === 'All' || 
               this.state.topic === '' || 
               feedback.topics.includes(this.state.topic);
    }

    private matchesDateRangeFilter(feedback: SRFeedbackPages): boolean {
        const dueDate = new Date(feedback.dueDate);
        const fromDate = this.state.fromDate ? new Date(this.state.fromDate) : null;
        const toDate = this.state.toDate ? new Date(this.state.toDate) : null;
        
        if (fromDate && dueDate < fromDate) return false;
        if (toDate) {
            toDate.setHours(23, 59, 59);
            if (dueDate > toDate) return false;
        }
        return true;
    }

    private matchesActiveFilter(feedback: SRFeedbackPages): boolean {
        return this.state.active === 'All' || 
               this.state.active === '' || 
               feedback.active === this.state.active;
    }

    private matchesYearFilter(feedback: SRFeedbackPages): boolean {
        return new Date(feedback.dueDate).getFullYear().toString() === this.state.activeYear;
    }

    private matchesSearchFilter(feedback: SRFeedbackPages): boolean {
        return this.state.searchInput === '' || 
               !this.state.searchInput || 
               feedback.name.toLowerCase().includes(this.state.searchInput.toLowerCase());
    }

    public getFilteredFeedbackItems(): SRFeedbackPages[] {
        return this.state.feedbacks.filter(feedback => 
            this.matchesEntityFilter(feedback) &&
            this.matchesTopicFilter(feedback) &&
            this.matchesDateRangeFilter(feedback) &&
            this.matchesActiveFilter(feedback) &&
            this.matchesYearFilter(feedback) &&
            this.matchesSearchFilter(feedback)
        );
    }

    private renderFilters() {
        return (
            <>
                <div className="row margin-top-10 margin-btm-10">
                    {this.renderEntityFilter()}
                    {this.renderTopicFilter()}
                    {this.renderDateFilters()}
                    {this.renderActiveFilter()}
                </div>
                <div className="row margin-top-10 margin-btm-10">
                    {this.renderSearchFilter()}
                </div>
            </>
        );
    }

    private renderEntityFilter() {
        return (
            <div className='col-12 col-md-3'>
                <label><h4>Entity</h4></label>
                <div className="dropdown">
                    <button className="btn-secondary form-select text-start" type="button" id="entityDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                        {this.state.entity}
                    </button>
                    <div className="dropdown-menu" aria-labelledby="entityDropdown">
                        <button className="btn btn-link dropdown-item" onClick={() => { this.filterEntity('All'); }}>All</button>
                        {
                            this.state.entityList.map((entity, i) => {
                                return (
                                    <button key={i} className="btn btn-link dropdown-item" onClick={() => { this.filterEntity(entity); }}>{entity}</button>
                                )
                            })
                        }
                    </div>
                </div>
            </div>
        );
    }

    private renderTopicFilter() {
        return (
            <div className='col-12 col-md-3'>
                <div className="dropdown">
                    <label><h4>Topic</h4></label>
                    <div className="dropdown">
                        <button className="btn-secondary form-select text-start" type="button" id="topicDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                            {this.state.topic}
                        </button>
                        <div className="dropdown-menu" aria-labelledby="topicDropdown">
                            <button className="btn btn-link dropdown-item" onClick={() => { this.filterTopic('All'); }}>All</button>
                            {
                                this.state.topicList.map((topic, i) => {
                                    return (
                                        <button key={i} className="btn btn-link dropdown-item" onClick={() => { this.filterTopic(topic); }}>{topic}</button>
                                    )
                                })
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderDateFilters() {
        return (
            <>
                <div className='col-12 col-md-2'>
                    <div className="dropdown">
                        <label><h4>From Date</h4></label>
                        <input ref={this.startDateRef} className="startDate form-control" />
                    </div>
                </div>
                <div className='col-12 col-md-2'>
                    <div className="dropdown">
                        <label><h4>To Date</h4></label>
                        <input ref={this.endDateRef} className="endDate form-control" />
                    </div>
                </div>
            </>
        );
    }

    private renderActiveFilter() {
        return (
            <div className='col-12 col-md-2'>
                <div className="dropdown">
                    <label><h4>Active/Expired</h4></label>
                    <div className="dropdown">
                        <button className="btn-secondary form-select text-start" type="button" id="activeDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                            {this.state.active}
                        </button>
                        <div className="dropdown-menu" aria-labelledby="activeDropdown">
                            <button className="btn btn-link dropdown-item" onClick={() => { this.filterActive('All'); }}>All</button>
                            <button className="btn btn-link dropdown-item" onClick={() => { this.filterActive('Active'); }}>Active</button>
                            <button className="btn btn-link dropdown-item" onClick={() => { this.filterActive('Expired'); }}>Expired</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderSearchFilter() {
        return (
            <div className='col-2 col-md-4'>
                <div className="pageSearch">
                    <input id="title-search-form-control"
                        className="form-control"
                        placeholder="Search by title..."
                        value={this.state.searchInput}
                        onChange={(e) => this.filterSearchInput(e.target.value)}
                    />
                </div>
            </div>
        );
    }

    public render() {
        // Cache filtered items to avoid calling twice
        const filteredFeedbackItems = this.getFilteredFeedbackItems();

        return (
            <div id='stakeholdFeedback'>
                <h3 className='blue margin-top-0 margin-btm-20'>
                    Filter Feedback By
                    {!this.filtersAreDefault() &&
                        <button className="btn btn-small btn-secondary" onClick={this.clearFilters} type="button" style={{ float: "right", padding: "10px 20px" }}>
                            <i className="fa fa-remove"></i>&nbsp;&nbsp;Reset Filters
                        </button>
                    }
                </h3>
                {this.renderFilters()}
                <div className="tab-content-block margin-btm-20">
                    <ul className="nav nav-tabs">
                        {
                            this.state.yearList.map((year, i) => {
                                return (
                                    <li key={i} className="nav-item">
                                        <a className={year === this.state.activeYear ? "nav-link active" : "nav-link"} data-bs-toggle="tab" href={'#year-' + year} onClick={() => { this.filterYear(year); }}>{year}</a>
                                    </li>
                                )
                            })
                        }
                    </ul>
                </div>
                <div className="accordionblock">
                    {this.state.loading &&
                        <div className="processingIndicator row margin-top-30"><i className="fa fa-spin fa-refresh"></i></div>
                    }
                    <div className="accordion-content">
                        <div className="accordion panel-group labelBorderTop" id="accordion">
                            {
                                filteredFeedbackItems.map((feedback, i) => {
                                    return (
                                        <StakeholderFeedbackItem key={i} Name={feedback.name} DueDate={feedback.dueDate} Description={feedback.description} Url={feedback.linkURL} Id={feedback.contentLink} />
                                    )
                                })
                            }
                            {
                                filteredFeedbackItems.length === 0 && !this.state.loading &&
                                <div className='panel'><div className='panel-heading text-left noItems'>There are no items that match this criteria</div></div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}
